import classNames from 'classnames';
import React, {useContext} from 'react';

import {UI_REGION} from '@udemy/browse-utils';
import {DiscoveryUnit, DiscoveryUnitItem} from '@udemy/discovery-api';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {
    AlternateHeadline,
    BundleUnit,
    CourseUnitSkeletonVerticalLayout,
    DiscoveryUnitsContainer,
    DiscoveryUnitsContainerStore,
    SkeletonCourseCarousel,
    SkeletonCourseList,
    SkeletonTitle,
    StudentQuoteUnit,
} from '@udemy/react-discovery-units';
import {ErrorBoundary} from '@udemy/react-error-boundary';
import {ExperimentationContext} from '@udemy/react-experimentation-provider';
import {ContentArea} from '@udemy/react-layout-components';
import {useUDData} from '@udemy/ud-data';

import {
    isMwebStandardizedMobileCourseCardsEnabled,
    resolveTopicThreeCardVerticalLayoutExperiment,
} from 'src/lib/experimentation-helper';

import {DiscoveryListContainer} from '../../category/components/browse/discovery-list-container/discovery-list-container.react-component';
import {RefundNotice} from '../../category/components/browse/notices/refund-notice.react-component';
import {PRESET_FREE, SOURCE_PAGE_TYPE} from '../constants';
import {FreeTopicProps} from '../free-topic.react-component';
import styles from '../topic.module.less';
import {isOrgTopicPage, isSubsTopicPage, TopicPageType} from '../types/page';
import {TopicPageExperimentVariants, TopicProps} from '../types/topic';
import {getPageObjectIdFromTopic} from '../utils';
import {
    AVAILABLE_UNIT_TYPES,
    AVAILABLE_UNIT_VIEW_TYPES,
    SkillsHubUnitWithProductCards,
    getAvailableDiscoveryUnitComponentsAndProps,
} from './topic-discovery-units';

function useDiscoveryLayoutIsMobile(isMobileProp: boolean | null | undefined): boolean {
    const mediaMobileMax = useMatchMedia('mobile-max');
    const {request} = useUDData();
    if (mediaMobileMax != null) {
        return mediaMobileMax;
    }
    return Boolean(request?.isMobile) || !!isMobileProp;
}

interface TopicDiscoveryListContainerWrapperProps extends TopicProps {
    pageType: TopicPageType;
    presetFilters?: {price: string[]};
    handlePageChange?: () => void;
}

export const TopicDiscoveryListContainerWrapper = (
    props: TopicDiscoveryListContainerWrapperProps,
) => {
    const {
        topic,
        pageType,
        subsCollectionIds,
        isMobile,
        highlightedCourseReview,
        presetValue,
        presetFilters,
        secondaryDescription,
        handlePageChange,
        fullQuickViewBoxFeature,
        topicCatSubcatMwebCardStandardizationFeature,
        threeCardVerticalLayoutExperimentIsEnabled: threeCardVerticalFromProps,
    } = props;

    const {featureVariantAssignments} = useContext(ExperimentationContext);
    const discoveryLayoutIsMobile = useDiscoveryLayoutIsMobile(isMobile);
    const courseCardSize = discoveryLayoutIsMobile ? 'small' : 'large';
    const topicCatSubcatMwebCardStandardizationEnabled = isMwebStandardizedMobileCourseCardsEnabled(
        topicCatSubcatMwebCardStandardizationFeature?.configuration,
    );
    const threeCardVerticalLayoutExperimentIsEnabled =
        resolveTopicThreeCardVerticalLayoutExperiment(
            threeCardVerticalFromProps,
            featureVariantAssignments,
        );
    const showThreeCardVerticalDiscoveryLayout =
        discoveryLayoutIsMobile && threeCardVerticalLayoutExperimentIsEnabled;

    const getSecondaryHeadingText = () => {
        if (presetValue === PRESET_FREE) {
            return gettext('All %(topicTitle)s tutorials');
        }
        return gettext('All %(topicTitle)s courses');
    };

    const renderDiscoveryListPromoComponents = () => {
        const topicBundleUnitProps: React.ComponentProps<typeof BundleUnit> = {
            pageType: 'topic-bundle',
            pageObjectId: topic.id,
            titleTypography: discoveryLayoutIsMobile ? 'ud-heading-lg' : 'ud-heading-xl',
            ...(showThreeCardVerticalDiscoveryLayout && {stackedCourseCardLayout: true}),
            // Render each course with the product-card layout so the per-card "Add to cart"
            // button — shipped via FbtBundleConfigProvider's app-level context — lands in the
            // card's `action` slot. Without this, the class BundleUnit falls back to
            // BrowseCourseCard (no action slot) and the button is appended after the card,
            // breaking the horizontal image-card layout. Depends on the surrounding ContentArea
            // below so the product card's `@container ud-content-area` query resolves its
            // expanded layout. Mirrors the cart success modal's FBT bundle, which couples the
            // same two flags (see @udemy/cart add-to-cart).
            useCourseProductCard: true,
            // Prefer the GraphQL bundle-service fetch and skip the redundant REST
            // discovery-units call entirely — this placement isn't part of the FBT
            // bundle-service experiment (FbtBundleConfigProvider doesn't set
            // useBundleServiceContent), so there's no fallback behavior to preserve.
            useBundleServiceContent: true,
            skipDiscoveryUnitFetch: true,
        };

        const topicBundleUnit = <BundleUnit {...topicBundleUnitProps} />;

        return [
            <FunnelLogContextProvider
                key="bundle-unit"
                data-item-index={9}
                context={SOURCE_PAGE_TYPE}
                subcontext={'learning-pack'}
                data-purpose="bundle-unit"
            >
                <ContentArea>{topicBundleUnit}</ContentArea>
            </FunnelLogContextProvider>,
            <StudentQuoteUnit
                key="student-quote-unit"
                data-item-index={6}
                topicTitle={topic.name}
                courseReview={highlightedCourseReview}
                data-purpose="student-quote-unit"
            />,
        ];
    };

    const verticalMobileBrowseLayoutExperimentProps = {
        showAddToCartInCourseCard: true,
        disableMobileAddToCartRedirect: true,
        cartButtonTextGoToCart: gettext('In your cart'),
        cartButtonDisableOnGoToCart: true,
        allowAddToCartSuccessModal: false,
    };

    return (
        <>
            <AlternateHeadline
                data-purpose="all-topic-courses"
                title={interpolate(getSecondaryHeadingText(), {topicTitle: topic.name}, true)}
                titleTag="h2"
                secondaryText={!isOrgTopicPage(pageType) ? secondaryDescription : undefined}
                layoutVariant={discoveryLayoutIsMobile ? 'compact' : 'default'}
                className={styles['secondary-heading']}
            />
            {!isSubsTopicPage(pageType) && <RefundNotice className={styles['refund-notice']} />}
            <DiscoveryListContainer
                pageType={pageType}
                pageSize={16}
                pageObjectId={getPageObjectIdFromTopic(topic)}
                presetFilters={presetFilters}
                courseCardSize={courseCardSize}
                onPageChange={handlePageChange}
                subsCollectionIds={subsCollectionIds}
                fullQuickView={fullQuickViewBoxFeature}
                query={topic.name}
                {...(discoveryLayoutIsMobile
                    ? {
                          ...verticalMobileBrowseLayoutExperimentProps,
                          ...(topicCatSubcatMwebCardStandardizationEnabled && {
                              showAddToCartInCourseCard: false,
                          }),
                      }
                    : {})}
            >
                {!isOrgTopicPage(pageType) && renderDiscoveryListPromoComponents()}
            </DiscoveryListContainer>
        </>
    );
};

export interface TopicDiscoveryUnitsContainerWrapperProps
    extends Pick<TopicProps, 'enrollmentStat' | 'topic' | 'isMobile'>,
        TopicPageExperimentVariants {
    children?: React.ReactNode;
    store?: DiscoveryUnitsContainerStore;
    pageType: string;
    discoveryUnitFilter: (unit: Partial<DiscoveryUnit>) => boolean;
    disableInfiniteScroll?: boolean;
    isStaticRender?: boolean;
    units?: DiscoveryUnit[];
    onLoadMore?: (title: string) => Promise<Partial<DiscoveryUnit>>;
    /** Controls which PP merchandising variant to render (`'general'` or `'cert'`). */
    ppMerchandisingVariant?: 'general' | 'cert';
}
export const TopicDiscoveryUnitsContainerWrapper = (
    props: TopicDiscoveryUnitsContainerWrapperProps,
) => {
    const {
        //----------
        topic,
        isMobile,
        store,
        pageType,
        children,
        discoveryUnitFilter,
        disableInfiniteScroll,
        units,
        isStaticRender,
        onLoadMore,
        ppMerchandisingVariant,
    } = props;

    const {gettext} = useI18n();
    const discoveryLayoutIsMobile = useDiscoveryLayoutIsMobile(isMobile);

    const getUnitComponentAndProps = (unit: Partial<DiscoveryUnit<DiscoveryUnitItem>>) => {
        const allCoursesInUserSubscription = () => {
            if (unit.item_type === 'course') {
                return unit.items?.every(
                    (item: DiscoveryUnitItem & {is_in_user_subscription?: boolean}) =>
                        item?.is_in_user_subscription,
                );
            }
            return false;
        };

        const singleCourseUnitExperimentProps = {
            allowAddToCartSuccessModal: false,
            showAddToCartInCourseCard: !allCoursesInUserSubscription,
            showQuickViewBox: false,
            disableMobileAddToCartRedirect: true,
            cartButtonTextGoToCart: gettext('In your cart'),
            cartButtonDisableOnGoToCart: true,
        };
        const stackedMobileLayoutExperimentProps = {
            layout: 'singlecol',
            disableInfiniteScroll: true,
            showInstructorDetails: true,
            maxCards: 12,
            maxCardsPerStack: 3,
            ...singleCourseUnitExperimentProps,
        };
        // TODO: GR-56 Remove after topic_cat_subcat_mweb_card_standardization concludes
        const verticalStackedRecoUnitsProps = {
            layout: 'singlecol' as const,
            showStackedProductCardLayout: true,
            disableInfiniteScroll: true,
            showAddToCartInCourseCard: false,
            addToCartButtonStyle: 'primary' as const,
            addToCartButtonSize: 'xsmall' as const,
            courseCardProps: {
                layout: 'vertical',
                showCourseTag: false,
                showDescription: false,
                showDetails: false,
                showRibbonsLast: true,
                disablePremiumHighlightedRibbon: true,
            },
            showInstructorDetails: true,
            isMobileMax: discoveryLayoutIsMobile,
            showQuickViewBox: false,
            maxCardsPerStack: 3,
            maxCards: 3,
            showDividers: false,
            loadingSkeleton: <CourseUnitSkeletonVerticalLayout />,
        };

        const unitComponentPropsByUnitType = {
            [AVAILABLE_UNIT_TYPES.BESTSELLER_LABELS]: {
                titleTypography: discoveryLayoutIsMobile ? 'ud-heading-lg' : 'ud-heading-xl',
            },
            [AVAILABLE_UNIT_TYPES.ALGORITHM_BASED_ASSESSMENTS_WITHOUT_FILTER]: {
                title: gettext('Evaluate your skills with assessments'),
                subtitle: gettext(
                    'Identify your proficiency level and get content recommendations based on your results.',
                ),
                uiRegion: UI_REGION.ASSESSMENT_UNIT_TOPIC,
                sourcePageType: SOURCE_PAGE_TYPE,
                sourcePageId: getPageObjectIdFromTopic(topic),
            },
            [AVAILABLE_UNIT_TYPES.ALGORITHM_BASED_LABS_WITHOUT_FILTER]: {
                title: gettext('Get hands-on practice with labs'),
                subtitle: gettext(
                    'Sharpen your technical skills with access to risk-free environments and real-world projects.',
                ),
                sourcePageType: SOURCE_PAGE_TYPE,
                sourcePageId: getPageObjectIdFromTopic(topic),
                uiRegion: UI_REGION.LAB_UNIT_TOPIC,
            },
            [AVAILABLE_UNIT_TYPES.TOP_INSTRUCTOR]: {
                componentName: 'popular_instructors_unit',
                alternateHeadline: {
                    title: unit.title,
                    secondaryText: gettext(
                        'These real-world experts are highly rated by learners like you.',
                    ),
                    secondaryTextClass: styles['alternate-headline__subtitle'],
                },

                showTitle: false,
            },
            [AVAILABLE_UNIT_TYPES.PERSONAL_PLAN_MERCHANDISING]: {
                topicName: topic.name,
                isMobile: discoveryLayoutIsMobile,
                variant: ppMerchandisingVariant,
            },
            [AVAILABLE_UNIT_VIEW_TYPES.TOPIC_SUBSCRIPTION_UPSELL]: {
                topicName: topic.name,
                isMobile: discoveryLayoutIsMobile,
                variant: ppMerchandisingVariant,
            },
            [AVAILABLE_UNIT_VIEW_TYPES.HORIZONTAL_TABBED]: {
                className: classNames('component-margin', {
                    [styles['primary-unit']]: discoveryLayoutIsMobile,
                }),
                // Shipped variant from mx_mobile_vertical_reco_units: always non-compact on mobile
                compact: false,
                gridCols: !discoveryLayoutIsMobile ? 4 : undefined,
                renderExperimentalCourseCards: false,
                // Hack to make statically-rendered course cards larger and ensure they're considered the LCP element.
                courseCardProps: isStaticRender
                    ? {className: styles['expanded-course-card']}
                    : undefined,
                alternateHeadline: {
                    title: unit.title,
                    secondaryText: gettext('Explore courses from experienced, real-world experts.'),
                    secondaryTextClass: styles['alternate-headline__subtitle'],
                },
                onLoadMore: onLoadMore
                    ? (subUnit: {title: string}) => onLoadMore(subUnit.title)
                    : undefined,
                showTitle: false,
                ...(discoveryLayoutIsMobile && stackedMobileLayoutExperimentProps),
                // Shipped variant from mx_mobile_vertical_reco_units: always applied on mobile
                ...(discoveryLayoutIsMobile && verticalStackedRecoUnitsProps),
            },
            [AVAILABLE_UNIT_TYPES.FEATURED_COURSE]: {
                alternateHeadline: {
                    title: unit.title,
                    secondaryText: gettext(
                        'Many learners enjoyed this highly rated course for its engaging content.',
                    ),
                    secondaryTextClass: styles['alternate-headline__subtitle'],
                },
                showTitle: false,
                ...(discoveryLayoutIsMobile && singleCourseUnitExperimentProps),
                // Shipped variant from mx_mobile_vertical_reco_units: always applied on mobile
                ...(discoveryLayoutIsMobile && verticalStackedRecoUnitsProps),
            },
            [AVAILABLE_UNIT_VIEW_TYPES.DEFAULT]: {
                fullWidth: discoveryLayoutIsMobile,
                showPager: !discoveryLayoutIsMobile,
                layout: 'singlerow',
                titleTypography: discoveryLayoutIsMobile ? 'ud-heading-lg' : undefined,
                showSubtitle: true,
                carouselProps: {
                    allowScroll: discoveryLayoutIsMobile,
                    ariaLabel: unit.title,
                    ariaRoleDescription: gettext('carousel'),
                },
                gridCols: !discoveryLayoutIsMobile ? 4 : undefined,
                renderExperimentalCourseCards: false,
                onLoadMore: onLoadMore?.bind(null, unit.title ?? ''),
                ...(discoveryLayoutIsMobile && stackedMobileLayoutExperimentProps),
                // Shipped variant from mx_mobile_vertical_reco_units: always applied on mobile
                ...(discoveryLayoutIsMobile && verticalStackedRecoUnitsProps),
            },
            [AVAILABLE_UNIT_VIEW_TYPES.CERTIFICATION_PRACTICE_EXAM_CAROUSEL]: {
                title: gettext('Practice Exams'),
            },
        };

        const result = getAvailableDiscoveryUnitComponentsAndProps(
            unit as DiscoveryUnit,
            unitComponentPropsByUnitType,
        );

        // Shipped variant from mx_mobile_vertical_reco_units: on mobile, wrap SkillsHub
        // with product-card context so cards render as vertical product cards.
        const viewType = (unit as DiscoveryUnit).view_type;
        if (discoveryLayoutIsMobile && viewType === 'horizontal_tabbed') {
            return {...result, component: SkillsHubUnitWithProductCards};
        }

        return result;
    };

    // `DiscoveryUnitsContainer.componentDidMount` reads an empty `units` array as "fetch them
    // myself" and fires a REST discovery-units request against its own defaults. The package
    // exposes no opt-out, so skip mounting it and render the injected children directly.
    if (!units?.length) {
        return <ErrorBoundary>{children}</ErrorBoundary>;
    }

    return (
        <ErrorBoundary>
            <DiscoveryUnitsContainer
                alwaysShowInjectedComponents={true}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                pageType={pageType}
                pageObjectId={getPageObjectIdFromTopic(topic)}
                getUnitComponentAndProps={getUnitComponentAndProps}
                disableInfiniteScroll={disableInfiniteScroll}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                filter={(unit) => discoveryUnitFilter(unit)}
                store={store}
                units={units}
                deviceType={discoveryLayoutIsMobile ? 'mobile' : 'desktop'}
            >
                {children}
            </DiscoveryUnitsContainer>
        </ErrorBoundary>
    );
};

export const TopicDiscoveryUnitsContainerWrapperLoader = ({isMobile}: {isMobile: boolean}) => {
    return (
        <>
            <SkeletonTitle data-item-index={0} />
            <SkeletonCourseCarousel courseCount={isMobile ? 3 : 6} />
            <SkeletonCourseList courseCount={4} />
            <SkeletonCourseList courseCount={4} />
        </>
    );
};

export interface FreeTopicDiscoveryUnitsContainerWrapperProps
    extends Pick<FreeTopicProps, 'topic' | 'isMobile'>,
        TopicPageExperimentVariants {
    children?: React.ReactNode;
    pageType: string;
    discoveryUnitFilter: (unit: Partial<DiscoveryUnit>) => boolean;
    units?: DiscoveryUnit[];
    onLoadMore?: (title: string) => Promise<Partial<DiscoveryUnit>>;
    disableInfiniteScroll?: boolean;
}
export const FreeTopicDiscoveryUnitsContainerWrapper = (
    props: FreeTopicDiscoveryUnitsContainerWrapperProps,
) => {
    const {gettext, interpolate} = useI18n();
    const {
        topic,
        isMobile,
        pageType,
        children,
        discoveryUnitFilter,
        units,
        onLoadMore,
        disableInfiniteScroll,
    } = props;

    const getUnitComponentAndProps = (unit: Partial<DiscoveryUnit<DiscoveryUnitItem>>) => {
        const unitComponentPropsByUnitType = {
            [AVAILABLE_UNIT_TYPES.BESTSELLER_LABELS]: {
                titleTypography: isMobile ? 'ud-heading-lg' : 'ud-heading-xl',
            },
            [AVAILABLE_UNIT_VIEW_TYPES.HORIZONTAL_TABBED]: {
                alternateHeadline: {
                    title: interpolate(
                        gettext('Get more with paid %(topic)s courses'),
                        {topic: props.topic.name},
                        true,
                    ),
                    secondaryText: gettext(
                        'Enroll in our in-depth courses from top-rated instructors',
                    ),
                },
                showTitle: false,
                onLoadMore: onLoadMore
                    ? (subUnit: {title: string}) => onLoadMore(subUnit.title)
                    : undefined,
            },
            [AVAILABLE_UNIT_TYPES.TOP_INSTRUCTOR]: {
                alternateHeadline: {
                    title: unit.title,
                    secondaryText: gettext(
                        'These real-world experts are highly rated by learners like you.',
                    ),
                    secondaryTextClass: styles['alternate-headline__subtitle'],
                },
                showTitle: false,
            },
            [AVAILABLE_UNIT_TYPES.FEATURED_COURSE]: {
                alternateHeadline: {
                    title: unit.title,
                    secondaryText: gettext(
                        'Many learners enjoyed this highly rated course for its engaging content.',
                    ),
                    secondaryTextClass: styles['alternate-headline__subtitle'],
                },
                showTitle: false,
            },
            [AVAILABLE_UNIT_VIEW_TYPES.DEFAULT]: {
                fullWidth: isMobile,
                showTitle: true,
                showPager: !isMobile,
                layout: isMobile ? 'multirow' : 'singlerow',
                allowScroll: isMobile,
                onLoadMore: onLoadMore?.bind(null, unit.title ?? ''),
            },
        };

        return getAvailableDiscoveryUnitComponentsAndProps(
            unit as DiscoveryUnit,
            unitComponentPropsByUnitType,
        );
    };

    // See the equivalent guard in TopicDiscoveryUnitsContainerWrapper above: an empty `units`
    // array makes DiscoveryUnitsContainer fall back to a REST discovery-units fetch.
    if (!units?.length) {
        return <ErrorBoundary>{children}</ErrorBoundary>;
    }

    return (
        <ErrorBoundary>
            <DiscoveryUnitsContainer
                alwaysShowInjectedComponents={true}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                pageType={pageType}
                pageObjectId={getPageObjectIdFromTopic(topic)}
                getUnitComponentAndProps={getUnitComponentAndProps}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                filter={(unit) => discoveryUnitFilter(unit)}
                units={units}
                disableInfiniteScroll={disableInfiniteScroll}
                deviceType={isMobile ? 'mobile' : 'desktop'}
            >
                {children}
            </DiscoveryUnitsContainer>
        </ErrorBoundary>
    );
};
